/**
 * StickyScoreCta — slim scroll-anchored bottom bar that reveals once the
 * hero calculator has scrolled out of the viewport. Lets mid- and bottom-
 * page visitors jump back to the score input without hunting for it.
 *
 * The bar targets the FreeScoreHero (id="ads-hero-calculator") via
 * IntersectionObserver, so it's automatically coupled to whichever hero
 * the landing page currently uses.
 */
import React, { useEffect, useState } from 'react';
import { ArrowUp, X, Zap } from 'lucide-react';

const HERO_ID = 'ads-hero-calculator';
// Session-scoped dismissal — the pill stays hidden for the rest of this
// tab session but re-appears on future visits (dismissals persisting
// forever would silently kill our best mid-page conversion nudge).
const DISMISS_KEY = 'execpresence.stickyScoreCtaDismissed';

export function StickyScoreCta() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (_e) {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return undefined;
    const hero = document.getElementById(HERO_ID);
    if (!hero || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        // Show the bar only once the hero is fully out of view (i.e. the
        // user has scrolled past it). We don't reveal it while it's still
        // partially visible so we don't compete with the hero's own CTA.
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px -20% 0px' },
    );
    io.observe(hero);
    return () => io.disconnect();
  }, [dismissed]);

  const scrollBack = () => {
    const hero = document.getElementById(HERO_ID);
    if (hero) {
      hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the URL input after scroll settles so the user can type immediately.
      setTimeout(() => {
        const input = hero.querySelector('input[data-testid="free-score-hero-url-input"]');
        input?.focus();
      }, 650);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const dismiss = (e) => {
    // Stop the click from also triggering the pill's scrollBack handler.
    e.stopPropagation();
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (_e) {
      /* storage disabled — dismissal still applies for this render tree */
    }
  };

  if (dismissed) return null;

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 transition-all duration-300 ease-out ${
        visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      data-testid="sticky-score-cta"
    >
      <div className="flex items-center gap-1.5 rounded-full border border-sky-400/40 bg-[#050b1a]/95 backdrop-blur-md pl-4 pr-2 py-2 shadow-[0_20px_60px_-20px_rgba(56,189,248,0.55)]">
        <button
          type="button"
          onClick={scrollBack}
          className="group flex items-center gap-3 focus:outline-none"
          data-testid="sticky-score-cta-button"
          aria-label="Get my free LinkedIn score"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">
            <Zap size={13} strokeWidth={2.4} aria-hidden="true" />
          </span>
          <span className="text-[13px] sm:text-sm font-medium text-slate-100">
            Get my free LinkedIn score
          </span>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-500 text-white group-hover:bg-sky-400 transition-colors">
            <ArrowUp size={14} strokeWidth={2.4} aria-hidden="true" />
          </span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="ml-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:text-slate-100 hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
          aria-label="Dismiss score reminder"
          data-testid="sticky-score-cta-dismiss"
        >
          <X size={14} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default StickyScoreCta;
